/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * MANUALLY CHANGE A VARIANT
 * ------------------------------------------------------------------------------------------------------------
 *
 * You may want to manually change the variant, and let the theme automatically adjust all the selectors. To do
 * that, you can get the DOM element of type "<product-variants>", and call the selectVariant method on it with
 * the variant ID.
 *
 * Example:
 *
 * const productVariantElement = document.querySelector('product-variants');
 * productVariantElement.selectVariant(12345);
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */

(function() {
  function formatWithDelimiters(number, precision, thousands, decimal) {
    if (isNaN(number) || number == null) {
      return "0";
    }

    var num = (number / 100).toFixed(precision);
    var parts = num.split(".");
    var dollarsAmount = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
    var centsAmount = parts[1] ? decimal + parts[1] : "";

    return dollarsAmount + centsAmount;
  }

  function formatMoney(cents) {
    var moneyFormat = (window.themeVariables && window.themeVariables.settings && window.themeVariables.settings.moneyFormat) || "{{amount}}";
    var formatString = moneyFormat || "{{amount}}";
    var value = "0";

    if (formatString.indexOf("amount_no_decimals") !== -1) {
      value = formatWithDelimiters(cents, 0, ".", ",");
    } else if (formatString.indexOf("amount_with_comma_separator") !== -1) {
      value = formatWithDelimiters(cents, 2, ".", ",");
    } else if (formatString.indexOf("amount_no_decimals_with_comma_separator") !== -1) {
      value = formatWithDelimiters(cents, 0, ".", ",");
    } else {
      value = formatWithDelimiters(cents, 2, ",", ".");
    }

    return formatString.replace(/\{\{\s*(\w+)\s*\}\}/, value);
  }

  function refreshBundleOptionPrice(optionLabel, variantPriceCents) {
    if (!optionLabel) {
      return;
    }

    var radio = optionLabel.querySelector('input[type="radio"]');

    if (!radio) {
      return;
    }

    var qty = parseInt(radio.getAttribute("data-qty"), 10) || 1;
    var discount = parseInt(radio.getAttribute("data-discount"), 10) || 0;
    var regularTotal = variantPriceCents * qty;
    var discountedTotal = Math.round(regularTotal * (100 - discount) / 100);
    var nowElement = optionLabel.querySelector(".pepe-bundle__price-now");
    var wasElement = optionLabel.querySelector(".pepe-bundle__price-was");

    if (nowElement) {
      nowElement.textContent = formatMoney(discountedTotal);
    }

    if (wasElement) {
      wasElement.textContent = formatMoney(regularTotal);
    }
  }

  function applyBundleSelection(bundleElement, selectedRadio) {
    if (!bundleElement || !selectedRadio) {
      return;
    }

    var formId = bundleElement.getAttribute("data-product-form-id");
    var quantity = parseInt(selectedRadio.getAttribute("data-qty"), 10) || 1;
    var selectedLabel = selectedRadio.getAttribute("data-label") || "";
    var selectedTier = selectedRadio.value || "";
    var selectedDiscount = parseInt(selectedRadio.getAttribute("data-discount"), 10) || 0;
    var quantityInput = document.querySelector('input[name="quantity"][form="' + formId + '"]');
    var lineItemPropertyInput = bundleElement.querySelector('input[type="hidden"][name^="properties["]');

    if (!quantityInput) {
      var formElement = document.getElementById(formId);

      if (formElement) {
        quantityInput = document.createElement("input");
        quantityInput.type = "hidden";
        quantityInput.name = "quantity";
        formElement.appendChild(quantityInput);
      }
    }

    if (quantityInput) {
      quantityInput.value = quantity;
      quantityInput.dispatchEvent(new Event("change", { bubbles: true }));
    }

    if (lineItemPropertyInput) {
      lineItemPropertyInput.value = selectedLabel;
    }

    bundleElement.setAttribute("data-selected-tier", selectedTier);
    bundleElement.setAttribute("data-selected-qty", String(quantity));
    bundleElement.setAttribute("data-selected-discount", String(selectedDiscount));

    bundleElement.querySelectorAll(".pepe-bundle__option").forEach(function(optionElement) {
      optionElement.classList.remove("is-selected");
    });

    var selectedOption = selectedRadio.closest(".pepe-bundle__option");

    if (selectedOption) {
      selectedOption.classList.add("is-selected");
    }

    document.dispatchEvent(new CustomEvent("pepe:bundle:selected", {
      detail: {
        formId: formId,
        tier: selectedTier,
        quantity: quantity,
        discountPercent: selectedDiscount
      }
    }));

    if (Array.isArray(window.dataLayer)) {
      window.dataLayer.push({
        event: "pepe_bundle_select",
        bundle_tier: selectedTier,
        bundle_quantity: quantity,
        bundle_discount_percent: selectedDiscount
      });
    }
  }

  function initBundleElement(bundleElement) {
    if (!bundleElement || bundleElement.dataset.pepeBundleReady === "true") {
      return;
    }

    bundleElement.dataset.pepeBundleReady = "true";

    var optionRadios = bundleElement.querySelectorAll('input[type="radio"][name^="pepe_bundle_"]');

    if (!optionRadios.length) {
      return;
    }

    optionRadios.forEach(function(radio) {
      radio.addEventListener("change", function() {
        applyBundleSelection(bundleElement, radio);
      });
    });

    var initiallyChecked = bundleElement.querySelector('input[type="radio"][name^="pepe_bundle_"]:checked') || optionRadios[0];

    if (initiallyChecked) {
      initiallyChecked.checked = true;
      applyBundleSelection(bundleElement, initiallyChecked);
    }
  }

  function initBundleWidgets(scope) {
    var root = scope || document;
    var bundleElements = root.querySelectorAll("[data-pepe-bundle]");

    bundleElements.forEach(function(bundleElement) {
      initBundleElement(bundleElement);
    });
  }

  document.addEventListener("DOMContentLoaded", function() {
    initBundleWidgets(document);
  });

  document.addEventListener("shopify:section:load", function(event) {
    initBundleWidgets(event.target);
  });

  document.addEventListener("variant:changed", function(event) {
    var variant = event && event.detail && event.detail.variant;
    var form = event && event.target;

    if (!variant || !form || !form.id) {
      return;
    }

    var selector = '[data-pepe-bundle][data-product-form-id="' + form.id + '"]';
    var bundleElement = document.querySelector(selector);

    if (!bundleElement) {
      return;
    }

    bundleElement.setAttribute("data-variant-price", variant.price);
    bundleElement.querySelectorAll(".pepe-bundle__option").forEach(function(optionLabel) {
      refreshBundleOptionPrice(optionLabel, variant.price);
    });
  });

  document.addEventListener("variant:added", function(event) {
    var form = event && event.target;

    if (!form || !form.id) {
      return;
    }

    var selector = '[data-pepe-bundle][data-product-form-id="' + form.id + '"]';
    var bundleElement = document.querySelector(selector);

    if (!bundleElement) {
      return;
    }

    var tier = bundleElement.getAttribute("data-selected-tier") || "";
    var quantity = parseInt(bundleElement.getAttribute("data-selected-qty"), 10) || 1;
    var discountPercent = parseInt(bundleElement.getAttribute("data-selected-discount"), 10) || 0;

    if (Array.isArray(window.dataLayer)) {
      window.dataLayer.push({
        event: "pepe_bundle_add_to_cart",
        bundle_tier: tier,
        bundle_quantity: quantity,
        bundle_discount_percent: discountPercent
      });
    }
  });

  function initProductStickyForms() {
    document.querySelectorAll("product-sticky-form").forEach(function(stickyForm) {
      if (stickyForm.hasAttribute("data-pepe-sticky-ready")) {
        return;
      }

      var paymentContainer = document.getElementById("MainPaymentContainer");
      var footer = document.querySelector(".shopify-section--footer");

      if (!paymentContainer) {
        stickyForm.hidden = true;
        return;
      }

      if (stickyForm.intersectionObserver) {
        stickyForm.intersectionObserver.disconnect();
      }

      var animationFrameRequested = false;

      function updateVisibility() {
        var paymentRect = paymentContainer.getBoundingClientRect();
        var footerRect = footer ? footer.getBoundingClientRect() : null;
        var isPaymentContainerPassed = paymentRect.bottom <= 0;
        var isFooterVisible = footerRect && footerRect.top < window.innerHeight && footerRect.bottom > 0;
        var isPocket = window.matchMedia(window.themeVariables.breakpoints.pocket).matches;

        stickyForm.hidden = !isPaymentContainerPassed || (isPocket && isFooterVisible);
      }

      function scheduleVisibilityUpdate() {
        if (animationFrameRequested) {
          return;
        }

        animationFrameRequested = true;
        window.requestAnimationFrame(function() {
          animationFrameRequested = false;
          updateVisibility();
        });
      }

      var observer = new IntersectionObserver(scheduleVisibilityUpdate);
      stickyForm.intersectionObserver = observer;
      observer.observe(paymentContainer);

      if (footer) {
        observer.observe(footer);
      }

      window.addEventListener("scroll", scheduleVisibilityUpdate, { passive: true });
      window.addEventListener("resize", scheduleVisibilityUpdate);
      stickyForm.pepeStickyCleanup = function() {
        observer.disconnect();
        window.removeEventListener("scroll", scheduleVisibilityUpdate);
        window.removeEventListener("resize", scheduleVisibilityUpdate);
      };

      stickyForm.setAttribute("data-pepe-sticky-ready", "");
      updateVisibility();
    });
  }
  document.addEventListener("DOMContentLoaded", initProductStickyForms);
  document.addEventListener("shopify:section:load", initProductStickyForms);
  document.addEventListener("shopify:section:unload", function(event) {
    var stickyForm = event.target.querySelector("product-sticky-form");

    if (stickyForm && stickyForm.pepeStickyCleanup) {
      stickyForm.pepeStickyCleanup();
    }
  });

})();

